import { createContext, useContext, type ComponentProps } from "react";
import useMediaQuery from "@/lib/hooks/use-media-query";
import DropdownMenu, {
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "../dropdown-menu/dropdown-menu";
import DrawerMenu, {
  DrawerMenuCheckboxItem,
  DrawerMenuContent,
  DrawerMenuGroup,
  DrawerMenuItem,
  DrawerMenuLabel,
  DrawerMenuPortal,
  DrawerMenuRadioGroup,
  DrawerMenuRadioItem,
  DrawerMenuSeparator,
  DrawerMenuShortcut,
  DrawerMenuSub,
  DrawerMenuSubContent,
  DrawerMenuSubTrigger,
  DrawerMenuTrigger,
} from "../drawer-menu/drawer-menu";

export const AdaptiveMenuContext = createContext(
  {} as {
    isDrawer?: boolean;
  },
);

export function useAdaptiveMenu() {
  const context = useContext(AdaptiveMenuContext);
  return context;
}

export default function AdaptiveMenu({
  ...props
}: ComponentProps<typeof DropdownMenu> & ComponentProps<typeof DrawerMenu>) {
  const isDrawer = !useMediaQuery("md");
  const Comp = isDrawer ? DrawerMenu : DropdownMenu;

  return (
    <AdaptiveMenuContext.Provider value={{ isDrawer }}>
      <Comp {...props} />
    </AdaptiveMenuContext.Provider>
  );
}

export function AdaptiveMenuTrigger({
  ...props
}: ComponentProps<typeof DropdownMenuTrigger> &
  ComponentProps<typeof DrawerMenuTrigger>) {
  const { isDrawer } = useAdaptiveMenu();
  const Comp = isDrawer ? DrawerMenuTrigger : DropdownMenuTrigger;

  return <Comp {...props} />;
}

export function AdaptiveMenuPortal({
  ...props
}: ComponentProps<typeof DropdownMenuPortal> &
  ComponentProps<typeof DrawerMenuPortal>) {
  const { isDrawer } = useAdaptiveMenu();
  const Comp = isDrawer ? DrawerMenuPortal : DropdownMenuPortal;

  return <Comp {...props} />;
}

export function AdaptiveMenuContent({
  align,
  sideOffset,
  ...props
}: ComponentProps<typeof DropdownMenuContent> &
  ComponentProps<typeof DrawerMenuContent>) {
  const { isDrawer } = useAdaptiveMenu();
  const Comp = isDrawer ? DrawerMenuContent : DropdownMenuContent;

  // Only pass align and sideOffset to DropdownMenuContent, not DrawerMenuContent
  if (isDrawer) {
    return <Comp {...props} />;
  }

  return <Comp align={align} sideOffset={sideOffset} {...props} />;
}

export function AdaptiveMenuGroup({
  ...props
}: ComponentProps<typeof DropdownMenuGroup> &
  ComponentProps<typeof DrawerMenuGroup>) {
  const { isDrawer } = useAdaptiveMenu();
  const Comp = isDrawer ? DrawerMenuGroup : DropdownMenuGroup;

  return <Comp {...props} />;
}

export function AdaptiveMenuItem({
  ...props
}: ComponentProps<typeof DropdownMenuItem> &
  ComponentProps<typeof DrawerMenuItem>) {
  const { isDrawer } = useAdaptiveMenu();
  const Comp = isDrawer ? DrawerMenuItem : DropdownMenuItem;

  return <Comp {...props} />;
}

export function AdaptiveMenuCheckboxItem({
  ...props
}: ComponentProps<typeof DropdownMenuCheckboxItem> &
  ComponentProps<typeof DrawerMenuCheckboxItem>) {
  const { isDrawer } = useAdaptiveMenu();
  const Comp = isDrawer ? DrawerMenuCheckboxItem : DropdownMenuCheckboxItem;

  return <Comp {...props} />;
}

export function AdaptiveMenuRadioGroup({
  ...props
}: ComponentProps<typeof DropdownMenuRadioGroup> &
  ComponentProps<typeof DrawerMenuRadioGroup>) {
  const { isDrawer } = useAdaptiveMenu();
  const Comp = isDrawer ? DrawerMenuRadioGroup : DropdownMenuRadioGroup;

  return <Comp {...props} />;
}

export function AdaptiveMenuRadioItem({
  ...props
}: ComponentProps<typeof DropdownMenuRadioItem> &
  ComponentProps<typeof DrawerMenuRadioItem>) {
  const { isDrawer } = useAdaptiveMenu();
  const Comp = isDrawer ? DrawerMenuRadioItem : DropdownMenuRadioItem;

  return <Comp {...props} />;
}

export function AdaptiveMenuLabel({
  ...props
}: ComponentProps<typeof DropdownMenuLabel> &
  ComponentProps<typeof DrawerMenuLabel>) {
  const { isDrawer } = useAdaptiveMenu();
  const Comp = isDrawer ? DrawerMenuLabel : DropdownMenuLabel;

  return <Comp {...props} />;
}

export function AdaptiveMenuSeparator({
  ...props
}: ComponentProps<typeof DropdownMenuSeparator> &
  ComponentProps<typeof DrawerMenuSeparator>) {
  const { isDrawer } = useAdaptiveMenu();
  const Comp = isDrawer ? DrawerMenuSeparator : DropdownMenuSeparator;

  return <Comp {...props} />;
}

export function AdaptiveMenuShortcut({
  ...props
}: ComponentProps<typeof DropdownMenuShortcut> &
  ComponentProps<typeof DrawerMenuShortcut>) {
  const { isDrawer } = useAdaptiveMenu();
  const Comp = isDrawer ? DrawerMenuShortcut : DropdownMenuShortcut;

  return <Comp {...props} />;
}

export function AdaptiveMenuSub({
  ...props
}: ComponentProps<typeof DropdownMenuSub> &
  ComponentProps<typeof DrawerMenuSub>) {
  const { isDrawer } = useAdaptiveMenu();
  const Comp = isDrawer ? DrawerMenuSub : DropdownMenuSub;

  return <Comp {...props} />;
}

export function AdaptiveMenuSubTrigger({
  ...props
}: ComponentProps<typeof DropdownMenuSubTrigger> &
  ComponentProps<typeof DrawerMenuSubTrigger>) {
  const { isDrawer } = useAdaptiveMenu();
  const Comp = isDrawer ? DrawerMenuSubTrigger : DropdownMenuSubTrigger;

  return <Comp {...props} />;
}

export function AdaptiveMenuSubContent({
  collisionPadding,
  ...props
}: ComponentProps<typeof DropdownMenuSubContent> &
  ComponentProps<typeof DrawerMenuSubContent>) {
  const { isDrawer } = useAdaptiveMenu();

  // Only pass collisionPadding to DropdownMenuSubContent, a drawer has no edges to avoid
  if (isDrawer) {
    return <DrawerMenuSubContent {...props} />;
  }

  return (
    <DropdownMenuSubContent collisionPadding={collisionPadding} {...props} />
  );
}
